import { QueryClient } from '@tanstack/react-query';
import { debugLog, debugWarn } from '../utils/debugLog';
import CachePerformanceService from './cachePerformanceService';
import { CacheCoordinationService } from './cacheCoordinationService';
import MetricsAggregationService from './MetricsAggregationService';

type PerformanceMemoryDetails = {
  usedJSHeapSize?: number;
};

const getPerformanceMemoryUsage = (): number => {
  if (typeof performance === 'undefined') {
    return 0;
  }

  const perf = performance as Performance & { memory?: PerformanceMemoryDetails };
  const usedHeapSize = perf.memory?.usedJSHeapSize;

  return typeof usedHeapSize === 'number' ? usedHeapSize : 0;
};

/**
 * Performance Service Manager - Singleton for global performance monitoring
 * 
 * Provides a single instance of all performance services that can be used
 * throughout the application for performance monitoring and dashboard integration.
 */
class PerformanceServiceManager {
  private static instance: PerformanceServiceManager | null = null;
  private cachePerformanceService: CachePerformanceService | null = null;
  private cacheCoordinationService: CacheCoordinationService | null = null;
  private metricsAggregationService: MetricsAggregationService | null = null;
  private queryClient: QueryClient | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): PerformanceServiceManager {
    if (!PerformanceServiceManager.instance) {
      PerformanceServiceManager.instance = new PerformanceServiceManager();
    }
    return PerformanceServiceManager.instance;
  }

  /**
   * Initialize the performance services with QueryClient
   */
  public initialize(queryClient: QueryClient): void {
    if (this.cachePerformanceService) {
      // Already initialized
      return;
    }

    try {
      this.queryClient = queryClient;
      
      // Initialize cache coordination service
      this.cacheCoordinationService = new CacheCoordinationService(queryClient);
      
      // Initialize cache performance service
      this.cachePerformanceService = new CachePerformanceService(
        queryClient, 
        this.cacheCoordinationService
      );

      // Initialize metrics aggregation service
      this.metricsAggregationService = new MetricsAggregationService(queryClient);

      debugLog('🚀 Performance services initialized');
    } catch (error) {
      debugWarn('⚠️ Failed to initialize performance services:', error);
      // Continue without performance monitoring
    }
  }

  /**
   * Get the cache performance service instance
   */
  public getCachePerformanceService(): CachePerformanceService | null {
    return this.cachePerformanceService;
  }

  /**
   * Get the cache coordination service instance
   */
  public getCacheCoordinationService(): CacheCoordinationService | null {
    return this.cacheCoordinationService;
  }

  /**
   * Get the metrics aggregation service instance
   */
  public getMetricsAggregationService(): MetricsAggregationService | null {
    return this.metricsAggregationService;
  }

  /**
   * Check if performance services are available
   */
  public isAvailable(): boolean {
    return this.cachePerformanceService !== null;
  }

  /**
   * Get basic performance metrics (safe fallback)
   */
  public getBasicMetrics() {
    if (!this.cachePerformanceService || !this.queryClient) {
      return {
        hits: 0,
        misses: 0,
        totalRequests: 0,
        hitRate: 0,
        missRate: 0,
        memoryUsage: getPerformanceMemoryUsage(),
        apiCalls: [],
        apiCallFrequency: 0,
        lastUpdated: new Date(),
      };
    }

    try {
      // Get comprehensive performance report
      const report = this.cachePerformanceService.generatePerformanceReport();
      
      // Extract basic metrics with NaN protection
      const totalHits = report.dataTypeMetrics.reduce((sum, m) => sum + (isNaN(m.cacheHits) ? 0 : m.cacheHits), 0);
      const totalMisses = report.dataTypeMetrics.reduce((sum, m) => sum + (isNaN(m.cacheMisses) ? 0 : m.cacheMisses), 0);
      const totalRequests = totalHits + totalMisses;
      
      // Calculate rates with NaN protection
      const hitRate = totalRequests > 0 && isFinite(totalRequests) ? (totalHits / totalRequests) * 100 : 0;
      const missRate = totalRequests > 0 && isFinite(totalRequests) ? (totalMisses / totalRequests) * 100 : 0;
      
      // Ensure all values are valid numbers
      const safeHitRate = isNaN(hitRate) || !isFinite(hitRate) ? 0 : hitRate;
      const safeMissRate = isNaN(missRate) || !isFinite(missRate) ? 0 : missRate;
      const safeMemoryUsage = isNaN(report.memoryMetrics.totalCacheSize) || !isFinite(report.memoryMetrics.totalCacheSize) 
        ? 0 
        : report.memoryMetrics.totalCacheSize;
      
      // Calculate API call frequency (calls per minute)
      const now = Date.now();
      const oneMinuteAgo = now - 60000;
      const allApiCalls = report.apiCallMetrics.flatMap(m => m.callHistory.map(c => c.timestamp.getTime()));
      const recentCalls = allApiCalls.filter(timestamp => timestamp > oneMinuteAgo);
      const apiCallFrequency = recentCalls.length;
      
      return {
        hits: totalHits,
        misses: totalMisses,
        totalRequests,
        hitRate: safeHitRate,
        missRate: safeMissRate,
        memoryUsage: safeMemoryUsage,
        apiCalls: allApiCalls,
        apiCallFrequency,
        lastUpdated: new Date(),
      };
    } catch (error) {
      debugWarn('⚠️ Error getting performance metrics:', error);
      return {
        hits: 0,
        misses: 0,
        totalRequests: 0,
        hitRate: 0,
        missRate: 0,
        memoryUsage: getPerformanceMemoryUsage(),
        apiCalls: [],
        apiCallFrequency: 0,
        lastUpdated: new Date(),
      };
    }
  }

  /**
   * Get aggregated metrics (enhanced data)
   */
  public getAggregatedMetrics() {
    if (!this.metricsAggregationService) {
      return null;
    }

    return this.metricsAggregationService.getCurrentMetrics();
  }

  /**
   * Get metrics history for trend analysis
   */
  public getMetricsHistory(limit?: number) {
    if (!this.metricsAggregationService) {
      return [];
    }

    return this.metricsAggregationService.getMetricsHistory(limit);
  }

  /**
   * Get active performance alerts
   */
  public getActiveAlerts() {
    if (!this.metricsAggregationService) {
      return [];
    }

    return this.metricsAggregationService.getActiveAlerts();
  }

  /**
   * Export performance data
   */
  public exportPerformanceData(startDate?: Date, endDate?: Date) {
    if (!this.metricsAggregationService) {
      return null;
    }

    return this.metricsAggregationService.exportMetrics(startDate, endDate);
  }

  /**
   * Add custom metric for tracking
   */
  public addCustomMetric(name: string, value: number): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.addCustomMetric(name, value);
    }
  }

  /**
   * Increment custom metric
   */
  public incrementCustomMetric(name: string, increment: number = 1): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.incrementCustomMetric(name, increment);
    }
  }

  /**
   * Record an API call for performance tracking
   */
  public recordAPICall(queryKey: string, duration: number, fromCache: boolean, dataSize: number = 0): void {
    if (this.cachePerformanceService) {
      try {
        this.cachePerformanceService.recordAPICall(queryKey, duration, fromCache, dataSize);
      } catch (error) {
        debugWarn('⚠️ Error recording API call:', error);
      }
    }
  }

  /**
   * Add metrics listener for real-time updates
   */
  public addMetricsListener(listener: (metrics: any) => void): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.addMetricsListener(listener);
    }
  }

  /**
   * Remove metrics listener
   */
  public removeMetricsListener(listener: (metrics: any) => void): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.removeMetricsListener(listener);
    }
  }

  /**
   * Add alert listener for performance alerts
   */
  public addAlertListener(listener: (alert: any) => void): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.addAlertListener(listener);
    }
  }

  /**
   * Remove alert listener
   */
  public removeAlertListener(listener: (alert: any) => void): void {
    if (this.metricsAggregationService) {
      this.metricsAggregationService.removeAlertListener(listener);
    }
  }

  /**
   * Destroy performance services (cleanup)
   */
  public destroy(): void {
    if (this.cachePerformanceService) {
      this.cachePerformanceService.destroy();
      this.cachePerformanceService = null;
    }
    
    if (this.metricsAggregationService) {
      this.metricsAggregationService.destroy();
      this.metricsAggregationService = null;
    }
    
    if (this.cacheCoordinationService) {
      // CacheCoordinationService doesn't have a destroy method, but we can null it
      this.cacheCoordinationService = null;
    }
    
    this.queryClient = null;
    debugLog('🔍 Performance services destroyed');
  }

  /**
   * Reset performance metrics
   */
  public resetMetrics(): void {
    if (this.cachePerformanceService) {
      try {
        this.cachePerformanceService.resetMetrics();
      } catch (error) {
        debugWarn('⚠️ Error resetting cache metrics:', error);
      }
    }

    if (this.metricsAggregationService) {
      try {
        this.metricsAggregationService.clearHistory();
        this.metricsAggregationService.clearAlerts();
      } catch (error) {
        debugWarn('⚠️ Error resetting aggregated metrics:', error);
      }
    }
    
    debugLog('🔄 Performance metrics reset');
  }
}

// Export singleton instance
export const performanceServiceManager = PerformanceServiceManager.getInstance();

// Export the class for testing
export default PerformanceServiceManager;
